import { useEffect, useMemo, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import {
  Avatar,
  type InputProps,
  ListItem,
  ListItemAvatar,
  ListItemText,
  debounce,
} from '@mui/material';
import { HouseIcon, UserIcon, UserSearchIcon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { type BuyerLeadSelectFragment } from '@graphql/__generated__/graphql';
import { RaAutoComplete } from '@modules/_shared/components/data-grid/RaAutoComplete';
import { SelectStartIcon } from '@modules/_shared/components/form/SelectStartIcon';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';

const SEARCH_BUYER_LEADS = gql(/* GraphQL */ `
  query SearchBuyerLeads($search: String!) {
    search_buyer_leads_tsv(args: { search_text: $search }) {
      ...BuyerLeadSelect
    }
  }

  fragment BuyerLeadSelect on buyer_leads {
    id
    user {
      id
      first_name
      last_name
    }
    lot {
      id
      lot_reference
      title
      property {
        street_number
        route
        postcode
        locality
        state
        country
      }
    }
  }
`);

const GET_BUYER_LEAD = gql(/* GraphQL */ `
  query GetBuyerLead($id: uuid!) {
    buyer_leads_by_pk(id: $id) {
      id
      ...BuyerLeadSelect
    }
  }
`);

type BuyerLeadSelectProps = {
  buyerLeadId?: string;
  onChange?: (buyerLeadId: string | null) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  InputProps?: InputProps;
};

const formatLotLabel = (lot: BuyerLeadSelectFragment['lot']) => {
  const lotParts: string[] = [];
  if (lot?.lot_reference) {
    lotParts.push(lot.lot_reference);
  }
  if (lot?.title) {
    lotParts.push(lot.title);
  } else if (lot?.property) {
    lotParts.push(formatAddress(lot.property, ', '));
  }
  return lotParts.join(' · ');
};

const formatBuyerLeadLabel = (buyerLead: BuyerLeadSelectFragment) => {
  const parts: string[] = [];
  const user = buyerLead.user;
  if (user?.first_name || user?.last_name) {
    parts.push([user?.first_name, user?.last_name].filter(Boolean).join(' '));
  }

  const lotLabel = formatLotLabel(buyerLead.lot);
  if (lotLabel !== '') {
    parts.push(lotLabel);
  }

  return parts.join(' — ');
};

export const BuyerLeadSelect = ({
  buyerLeadId,
  onChange,
  autoFocus = false,
  disabled = false,
  InputProps,
}: BuyerLeadSelectProps) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<BuyerLeadSelectFragment[]>([]);
  const [selectedValue, setSelectedValue] =
    useState<BuyerLeadSelectFragment | null>(null);

  const { data: defaultBuyerLead } = useQuery(GET_BUYER_LEAD, {
    skip: !buyerLeadId,
    variables: { id: buyerLeadId ?? '' },
  });

  const defaultNode = defaultBuyerLead?.buyer_leads_by_pk ?? null;

  useEffect(() => {
    if (defaultNode != null) {
      setSelectedValue(defaultNode);
    } else if (buyerLeadId == null) {
      setSelectedValue(null);
    }
  }, [buyerLeadId, defaultNode]);

  const [searchBuyerLeads, { loading, error }] = useLazyQuery(
    SEARCH_BUYER_LEADS,
    { notifyOnNetworkStatusChange: true },
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        const searchValue = value.trim();

        if (searchValue === '') {
          setOpen(false);
          setOptions([]);
          return;
        }

        searchBuyerLeads({
          variables: { search: searchValue },
        })
          .then(response => {
            setOpen(true);
            setOptions(response.data?.search_buyer_leads_tsv ?? []);
          })
          .catch(() => {
            setOpen(false);
            setOptions([]);
          });
      }, 275),
    [searchBuyerLeads],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.clear();
    };
  }, [debouncedSearch]);

  return (
    <RaAutoComplete<BuyerLeadSelectFragment>
      InputProps={{
        ...InputProps,
        startAdornment: InputProps?.startAdornment ?? (
          <SelectStartIcon icon={UserSearchIcon} />
        ),
        placeholder: InputProps?.placeholder ?? t('Search'),
      }}
      open={open}
      setOpen={setOpen}
      options={options}
      setOptions={setOptions}
      loading={loading}
      error={error != null}
      value={selectedValue}
      autoFocus={autoFocus}
      disabled={disabled}
      debouncedSearch={debouncedSearch}
      onChange={(_event, value) => {
        setSelectedValue(value);
        onChange?.(value?.id ?? null);
      }}
      getOptionLabel={option => formatBuyerLeadLabel(option)}
      renderOption={(props, option) => {
        const lotLabel = formatLotLabel(option.lot);
        const secondaryText = lotLabel || undefined;

        // Dynamic icon based on what data is available
        const hasUserData = option.user?.first_name || option.user?.last_name;
        const Icon = hasUserData ? UserIcon : HouseIcon;

        return (
          <ListItem {...props} key={option.id} dense>
            <ListItemAvatar>
              <Avatar>
                <Icon size={20} />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                hasUserData
                  ? [option.user?.first_name, option.user?.last_name]
                      .filter(Boolean)
                      .join(' ')
                  : t('No contact')
              }
              secondary={secondaryText}
            />
          </ListItem>
        );
      }}
    />
  );
};

export default BuyerLeadSelect;
